import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Box, Button, Typography, Tab, Tabs, Dialog, DialogContent, Stack, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import AgGridTable from '../../../components/table/AgGridTable';
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared"
import WhiteTooltip from '../../../components/WhiteTooltip';
import useRolePermissions from '../../../shared/hooks/useRolePermissions';
import { ROLES_ROUTE } from '../../../shared/constants/constant-data';
import { ICellRendererParams } from 'ag-grid-community';
import { ScheduleActionMenu } from '../../onPremises/scansV2/SchedulesTableView';
import CancelIcon from '@mui/icons-material/Cancel';

interface Schedule {
  schedule_id: string | null;
  schedule_status: string | null;
  frequency_name: string | null;
  time: string | null;
  date: string | null;
  frequency: string | null;
}

interface Scan {
  scan_id: string | null;
  scan_name: string;
  scan_type: string;
  status: number | null;
  date_added: string | null;
  agent_id: string;
  ip_range: string | string[] | null;
  is_scheduled: boolean;
  schedule: Schedule;
}

interface ScheduledScan {
  schedule_name: string;
  status: null;
  date_added: null;
  scan_type: string;
  agent_id: string;
  ip_range: string | string[] | null;
  is_scheduled: boolean;
  schedule: Schedule;
}

interface LinkedScansDrawerProps {
  agent: any;
  onClose: () => void;
  onOpenNewScan: () => void;
  onEditSchedule?: (data: any, refreshFn?: () => void) => void;
  onCancelSchedule?: (data: any) => void;
  onPauseSchedule?: (data: any) => void;
  onResumeSchedule?: (data: any) => void;
}

const STATUS_LABEL_MAP: Record<number, string> = {
  1: "In Progress",
  2: "In Progress",
  3: "Success",
  4: "Failed",
  5: "Cancelled",
  6: "Partially Success",
};

const STATUS_STYLE_MAP: Record<string, { color: string; background: string; border: string }> = {
  Success: { color: "#00a61b", background: "rgba(0, 166, 27, 0.04)", border: "1.5px solid #00a61b" },
  Failed: { color: "#f53b3b", background: "rgba(245, 59, 59, 0.04)", border: "1.5px solid #f53b3b" },
  Cancelled: { color: "#696595", background: "rgba(105, 101, 149, 0.04)", border: "1.5px solid #696595" },
  Completed: { color: "#696595", background: "rgba(105, 101, 149, 0.04)", border: "1.5px solid #696595" },
  "Partially Success": { color: "#F7941F", background: "rgba(247, 148, 31, 0.04)", border: "1.5px solid #F7941F" },
  Paused: { color: "#F7941F", background: "rgba(247, 148, 31, 0.04)", border: "1.5px solid #F7941F" },
  "In Progress": { color: "#696595", background: "rgba(58, 42, 218, 0.04)", border: "1.5px solid #696595" },
};

const DEFAULT_STYLE = { color: "#9E9E9E", background: "rgba(158, 158, 158, 0.04)", border: "1.5px solid #9E9E9E" };

const StatusBadge: React.FC<{ value: any }> = ({ value }) => {
  if (value === null || value === undefined) return <span style={{ color: '#9E9E9E' }}>-</span>;

  const rawStatus = (value as any)?.label ?? value;
  const statusCode =
    typeof rawStatus === "number"
      ? rawStatus
      : typeof rawStatus === "string" && rawStatus.trim() !== "" && !Number.isNaN(Number(rawStatus))
        ? Number(rawStatus)
        : null;

  const label =
    statusCode !== null ? (STATUS_LABEL_MAP[statusCode] ?? String(statusCode)) : String(rawStatus);
  const { color, background, border } = STATUS_STYLE_MAP[label] ?? DEFAULT_STYLE;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "0 10px",
        borderRadius: "5px",
        fontWeight: 700,
        fontSize: "13px",
        color,
        background,
        border,
        minWidth: "80px",
        textAlign: "center",
        height: "24px",
        lineHeight: "20px",
        boxSizing: "border-box",
      }}
    >
      <span
        style={{
          width: 9,
          height: 9,
          borderRadius: "50%",
          backgroundColor: color,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
};

const scheduleStatusStyles: Record<string, { color: string; background: string; border: string }> = {
  Active: { color: "#00a61b", background: "rgba(0, 166, 27, 0.04)", border: "1.5px solid #00a61b" },
  Inactive: { color: "#f53b3b", background: "rgba(245, 59, 59, 0.04)", border: "1.5px solid #f53b3b" },
};

const ScheduleStatusBadge: React.FC<{ value: string | null }> = ({ value }) => {
  if (!value) return <span style={{ color: '#9E9E9E' }}>-</span>;
  const styles = scheduleStatusStyles[value] ?? DEFAULT_STYLE;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "0 10px",
        borderRadius: "5px",
        fontWeight: 700,
        fontSize: "13px",
        ...styles,
        minWidth: "80px",
        textAlign: "center",
        height: "24px",
        lineHeight: "20px",
        boxSizing: "border-box",
      }}
    >
      <span style={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: styles.color, display: "inline-block" }} />
      {value}
    </span>
  );
};

const ipRangeFormatter = (params: any) => {
  if (!params.value) return '-';
  try {
    if (Array.isArray(params.value)) return params.value.join(', ');
    const parsed = JSON.parse(params.value);
    if (Array.isArray(parsed)) return parsed.join(', ');
  } catch {
    // ignore
  }
  return params.value;
};

const LinkedScansDrawer: React.FC<LinkedScansDrawerProps> = (props) => {
  const { agent, onClose: _onClose, onOpenNewScan } = props;
  const { hasPermissions } = useRolePermissions();
  const [allScans, setAllScans] = useState<Scan[]>([]);
  const [scheduledScans, setScheduledScans] = useState<ScheduledScan[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isConfirming, setIsConfirming] = useState(false);

  // Pause/Resume/Cancel confirmation state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: 'pause' | 'resume' | 'cancel' | null;
    data: any;
  }>({ open: false, type: null, data: null });

  const fetchLinkedScans = useCallback(async () => {
    if (!agent?.id) return;
    setIsLoading(true);
    try {
      const timezone = "Asia/Calcutta";

      const response = await axiosInstance.get(`${END_POINTS.NewAGENT_LINKED_SCANS}/${agent.id}/scans?timezone=${timezone}`);
      if (response.status === 200) {
        setAllScans(response.data?.data?.all_scans || []);
        setScheduledScans(response.data?.data?.scheduled_scans || []);
      }
    } catch (error) {
      console.error('Error fetching linked scans:', error);
    } finally {
      setIsLoading(false);
    }
  }, [agent?.id]);

  const handleEditSchedule = (data: any) => {
    if (props.onEditSchedule) {
      props.onEditSchedule(data, fetchLinkedScans);
    } else {
      console.log("Edit Schedule:", data);
      // Fallback or default logic if needed
    }
  };

  const handleCancelSchedule = (data: any) => {
    setConfirmDialog({ open: true, type: 'cancel', data });
  };

  const handlePauseSchedule = (data: any) => {
    setConfirmDialog({ open: true, type: 'pause', data });
  };

  const handleResumeSchedule = (data: any) => {
    setConfirmDialog({ open: true, type: 'resume', data });
  };

  const handleConfirmAction = async () => {
    const { type, data } = confirmDialog;
    if (!type || !data) return;

    setIsConfirming(true);
    try {
      const scheduleId = data.schedule?.schedule_id || data.schedule_id;
      if (!scheduleId) {
        toast.error("Schedule ID not found");
        return;
      }

      let response;
      if (type === 'cancel') {
        response = await axiosInstance.post(END_POINTS.CANCEL_SCHEDULE, { schedule_id: scheduleId });
      } else {
        // Pause or Resume
        response = await axiosInstance.put(END_POINTS.DISCOVERY_EDIT_SCHEDULE, {
          schedule_id: scheduleId,
          status: type === 'resume'
        });
      }

      if (response.status === 200 || response.status === 201) {
        toast.success(`Schedule ${type}ed successfully`);
        fetchLinkedScans();
      }
    } catch (error: any) {
      console.error(`Error ${type}ing schedule:`, error);
      toast.error(error?.response?.data?.message || `Failed to ${type} schedule`);
    } finally {
      setIsConfirming(false);
      setConfirmDialog({ open: false, type: null, data: null });
    }
  };

  useEffect(() => {
    fetchLinkedScans();
  }, [fetchLinkedScans]);

  const allScansColumnDefs = useMemo(() => [
    { field: 'scan_name', headerName: 'Scan Name', flex: 1, minWidth: 150, cellRenderer: (params: any) => <WhiteTooltip title={params.value} arrow>{params.value}</WhiteTooltip> },
    { field: 'scan_type', headerName: 'Type', minWidth: 170, cellRenderer: (params: any) => <WhiteTooltip title={params.value} arrow>{params.value}</WhiteTooltip> },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 160,
      cellRenderer: (params: any) => <StatusBadge value={params.value} />,
    },

    {
      field: 'ip_range',
      headerName: 'Target',
      flex: 1,
      minWidth: 150,
      valueFormatter: ipRangeFormatter,
      cellRenderer: (params: any) => {
        const displayValue = Array.isArray(params.value) ? params.value.join(', ') : (params.valueFormatted || params.value);
        return <WhiteTooltip title={displayValue} arrow>{displayValue}</WhiteTooltip>;
      }
    },
    {
      field: 'is_scheduled',
      headerName: 'Scheduled',
      minWidth: 140,
      cellRenderer: (params: any) => {
        console.log("params", params.value);
        return params.value == "1" ? "Yes" : "No";
      }
    },

    // {
    //   field: 'schedule.frequency_name',
    //   headerName: 'Frequency',
    //   minWidth: 130,
    //   valueFormatter: (params: any) => params.value || '-',
    // },


  ], []);

  const scheduledScansColumnDefs = useMemo(() => [
    { field: 'schedule_name', headerName: 'Schedule Name', flex: 1, minWidth: 160, cellRenderer: (params: any) => <WhiteTooltip title={params.value} arrow>{params.value}</WhiteTooltip> },
    { field: 'scan_type', headerName: 'Type', minWidth: 110, cellRenderer: (params: any) => <WhiteTooltip title={params.value} arrow>{params.value}</WhiteTooltip> },
    {
      field: 'schedule.schedule_status',
      headerName: 'Status',
      minWidth: 155,
      cellRenderer: (params: any) => <ScheduleStatusBadge value={params.value} />,
    },
    {
      field: 'schedule.time',
      headerName: 'Schedule Time',
      minWidth: 150,
      valueFormatter: (params: any) => params.value || '-',
    },
    {
      field: 'schedule.date',
      headerName: 'Schedule Date',
      minWidth: 180,
      hide: true,
      valueFormatter: (params: any) => params.value || '-',
    },
    {
      field: 'schedule.frequency_name',
      headerName: 'Frequency',
      minWidth: 130,
      hide: true,
      valueFormatter: (params: any) => params.value || '-',
    },
    {
      field: 'ip_range',
      headerName: 'Target',
      flex: 1,
      minWidth: 140,
      valueFormatter: ipRangeFormatter,
      cellRenderer: (params: any) => {
        const displayValue = Array.isArray(params.value) ? params.value.join(', ') : (params.valueFormatted || params.value);
        return <WhiteTooltip title={displayValue} arrow>{displayValue}</WhiteTooltip>;
      }
    },
    {
      headerName: "",
      field: "actions",
      filter: false,
      sortable: false,
      minWidth: 40,

      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data) return null;
        return <ScheduleActionMenu params={params}
          onEditSchedule={handleEditSchedule}
          onCancelSchedule={handleCancelSchedule}
          onPauseSchedule={handlePauseSchedule}
          onResumeSchedule={handleResumeSchedule}
        />;
      },
    },


  ], []);

  const headerActions = useMemo(() => (
    <Box sx={{ display: 'flex', gap: 1.5, pr: 1 }}>
      {hasPermissions(ROLES_ROUTE.NEW_SCAN) && (
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={agent?.status_display !== "Active"}
          onClick={onOpenNewScan}
          sx={{
            textTransform: 'none',
            px: 2.5,
            py: 0.75,
            fontWeight: 500,
            bgcolor: '#3A2ADA',
            borderRadius: 2,
            '&:hover': {
              bgcolor: '#2F1FBD',
            },
          }}
        >
          + New Scan
        </Button>
      )}
    </Box>
  ), [hasPermissions, agent?.status_display, onOpenNewScan]);

  return (
    <Box className='scroll' sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2.5, borderBottom: '1px solid #E2E2F2' }}>
        <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Linked Scans & Schedules</Typography>
        <IconButton
          onClick={_onClose}
          sx={{
            color: 'gray',
            transition: 'all 0.2s ease-in-out',
            p: 0.5,
            '&:hover': {
              color: 'gray',
              transform: 'scale(1.1)',
            },
          }}
        >
          <CancelIcon />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid #E2E2F2', px: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, newValue) => setActiveTab(newValue)}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13,
              minHeight: 40,
              color: '#6B7280',
            },
            '& .Mui-selected': {
              color: '#3A2ADA !important',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#3A2ADA',
            },
          }}
        >
          <Tab label={`Scans (${allScans.length})`} id="tab-all-scans" aria-controls="tabpanel-all-scans" />
          <Tab label={`Scheduled Scans (${scheduledScans.length})`} id="tab-scheduled-scans" aria-controls="tabpanel-scheduled-scans" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {activeTab === 0 && (
          <AgGridTable
            columnDefs={allScansColumnDefs as any}
            data={allScans as any}
            showSearch
            headerActions={headerActions}
            height="calc(100% - 8px)"
            pagination={true}
            isLoading={isLoading}
          />
        )}
        {activeTab === 1 && (
          <AgGridTable
            columnDefs={scheduledScansColumnDefs as any}
            data={scheduledScans as any}
            showAppsIcon={true}
            showSearch
            headerActions={headerActions}
            height="calc(100% - 8px)"
            pagination={true}
            isLoading={isLoading}
          />
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: null, data: null })}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            padding: "16px",
            minWidth: "280px",
            maxWidth: "320px",
            textAlign: "center",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Stack spacing={2} alignItems="center">
            <Typography variant="body1" sx={{ fontWeight: 600, color: "#1a1a1a", fontSize: "1rem", lineHeight: 1.4 }}>
              Are you sure want to {confirmDialog.type === 'cancel' ? 'Cancel' : confirmDialog.type === 'pause' ? 'Pause' : 'Resume'} “
              {confirmDialog.data?.schedule_name || confirmDialog.data?.scan_name}”?
            </Typography>

            <Stack direction="row" spacing={1.5} sx={{ width: "100%" }}>
              <Button
                variant="outlined"
                fullWidth
                size="small"
                disabled={isConfirming}
                onClick={() => setConfirmDialog({ open: false, type: null, data: null })}
                sx={{
                  borderRadius: "6px",
                  borderColor: "#ddd",
                  color: "#666",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  py: 0.6,
                  "&:hover": { borderColor: "#bbb" },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                size="small"
                disabled={isConfirming}
                onClick={handleConfirmAction}
                sx={{
                  borderRadius: "6px",
                  backgroundColor: "#3A2ADA",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  py: 0.6,
                  "&:hover": { backgroundColor: "#2E1FBF" },
                }}
              >
                Confirm
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LinkedScansDrawer;
